import { useState, useEffect } from "react";
import { api } from "@/App";
import { Bug, Database, Clock, Users, ChevronUp, ChevronDown, RefreshCw, Terminal, Trash2 } from "lucide-react";

export default function DebugInfo() {
  const [debugData, setDebugData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);

  const fetchDebugInfo = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/debug/info");
      setDebugData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Failed to fetch debug info");
      console.error('[DEBUG] Failed to fetch debug info:', err);
    } finally {
      setLoading(false);
    }
  };

  // Refresh logs from global storage
  const refreshLogs = () => {
    setLogs([...(window.__debugLogs || [])]);
  };

  useEffect(() => {
    fetchDebugInfo();
    refreshLogs();
    
    // Poll for new logs every second when expanded
    const interval = setInterval(() => {
      if (window.__debugLogs) {
        setLogs([...window.__debugLogs]);
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  const clearLogs = () => {
    if (window.__debugLogs) {
      window.__debugLogs.length = 0;
    }
    setLogs([]);
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'error': return 'text-red-400';
      case 'warn': return 'text-yellow-400';
      default: return 'text-slate-300';
    }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50">
      {/* Collapsed bar */}
      <div 
        className={`bg-slate-800 text-white px-4 py-2 flex items-center justify-between cursor-pointer hover:bg-slate-700 transition-colors ${expanded ? 'rounded-t-lg' : ''}`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <Bug className="h-3.5 w-3.5 text-amber-400" />
            <span className="font-medium">Debug</span>
          </div>
          
          {debugData && (
            <>
              <div className="flex items-center gap-1.5 text-slate-300">
                <Clock className="h-3 w-3" />
                <span>Build: {debugData.build?.version} ({new Date(debugData.build?.timestamp).toLocaleString()})</span>
              </div>
              
              <div className="flex items-center gap-1.5">
                <Database className={`h-3 w-3 ${debugData.database?.status === 'connected' ? 'text-emerald-400' : 'text-red-400'}`} />
                <span className={debugData.database?.status === 'connected' ? 'text-emerald-400' : 'text-red-400'}>
                  DB: {debugData.database?.status}
                </span>
              </div>
              
              <div className="flex items-center gap-1.5 text-slate-300">
                <Users className="h-3 w-3" />
                <span>{debugData.database?.counts?.users} users</span>
              </div>
            </>
          )}

          {/* Log count indicator */}
          <div className="flex items-center gap-1.5">
            <Terminal className="h-3 w-3 text-cyan-400" />
            <span className="text-cyan-400">{logs.length} logs</span>
            {logs.some(l => l.type === 'error') && (
              <span className="bg-red-500 text-white px-1.5 py-0.5 rounded text-[10px]">
                {logs.filter(l => l.type === 'error').length} errors
              </span>
            )}
          </div>
          
          {error && (
            <span className="text-red-400">API Error: {error}</span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              fetchDebugInfo();
            }}
            className="p-1 hover:bg-slate-600 rounded"
            title="Refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
        </div>
      </div>

      {/* Expanded panel */}
      {expanded && (
        <div className="bg-slate-900 text-white text-xs font-mono">
          {/* Tabs */}
          <div className="flex border-b border-slate-700">
            <button
              onClick={() => setActiveTab('info')}
              className={`px-4 py-2 ${activeTab === 'info' ? 'bg-slate-800 text-amber-400' : 'text-slate-400 hover:text-white'}`}
            >
              <Database className="h-3.5 w-3.5 inline mr-1.5" />
              System Info
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`px-4 py-2 ${activeTab === 'logs' ? 'bg-slate-800 text-cyan-400' : 'text-slate-400 hover:text-white'}`}
            >
              <Terminal className="h-3.5 w-3.5 inline mr-1.5" />
              Console Logs
              {logs.some(l => l.type === 'error') && (
                <span className="ml-1.5 bg-red-500 text-white px-1.5 py-0.5 rounded text-[10px]">
                  {logs.filter(l => l.type === 'error').length}
                </span>
              )}
            </button>
          </div>

          {/* Info Tab */}
          {activeTab === 'info' && debugData && (
            <div className="p-4 max-h-64 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Build Info */}
                <div>
                  <h3 className="text-amber-400 font-semibold mb-2 flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" /> Build Info
                  </h3>
                  <div className="space-y-1 text-slate-300">
                    <p>Version: <span className="text-white">{debugData.build?.version}</span></p>
                    <p>Timestamp: <span className="text-white">{debugData.build?.timestamp}</span></p>
                    <p>Environment: <span className="text-white">{debugData.build?.environment}</span></p>
                  </div>
                </div>

                {/* Database Info */}
                <div>
                  <h3 className="text-amber-400 font-semibold mb-2 flex items-center gap-1">
                    <Database className="h-3.5 w-3.5" /> Database
                  </h3>
                  <div className="space-y-1 text-slate-300">
                    <p>Status: <span className={debugData.database?.status === 'connected' ? 'text-emerald-400' : 'text-red-400'}>{debugData.database?.status}</span></p>
                    <p>URL: <span className="text-white">{debugData.database?.mongo_url}</span></p>
                    <p>DB Name: <span className="text-white">{debugData.database?.db_name}</span></p>
                  </div>
                </div>

                {/* Data Counts */}
                <div>
                  <h3 className="text-amber-400 font-semibold mb-2">Data Counts</h3>
                  <div className="space-y-1 text-slate-300">
                    <p>Users: <span className="text-white">{debugData.database?.counts?.users}</span></p>
                    <p>Categories: <span className="text-white">{debugData.database?.counts?.categories}</span></p>
                    <p>Budget Goals: <span className="text-white">{debugData.database?.counts?.budget_goals}</span></p>
                    <p>Spending Records: <span className="text-white">{debugData.database?.counts?.spending_data}</span></p>
                  </div>
                </div>

                {/* Users List */}
                <div>
                  <h3 className="text-amber-400 font-semibold mb-2 flex items-center gap-1">
                    <Users className="h-3.5 w-3.5" /> Users in Database
                  </h3>
                  <div className="space-y-1">
                    {debugData.database?.users?.length > 0 ? (
                      debugData.database.users.map((user, idx) => (
                        <p key={idx} className="text-slate-300">
                          <span className="text-white font-medium">{user.username}</span>
                          {user.display_name && <span className="text-slate-500"> ({user.display_name})</span>}
                        </p>
                      ))
                    ) : (
                      <p className="text-red-400">No users found!</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Logs Tab */}
          {activeTab === 'logs' && (
            <div className="max-h-64 overflow-y-auto">
              <div className="sticky top-0 bg-slate-800 px-4 py-2 flex justify-between items-center border-b border-slate-700">
                <span className="text-slate-400">Console output ({logs.length} entries)</span>
                <button
                  onClick={clearLogs}
                  className="text-slate-400 hover:text-white flex items-center gap-1"
                >
                  <Trash2 className="h-3 w-3" />
                  Clear
                </button>
              </div>
              <div className="p-2">
                {logs.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No logs yet. Try logging in to see activity.</p>
                ) : (
                  logs.map((log, idx) => (
                    <div key={idx} className={`py-1 px-2 hover:bg-slate-800 rounded ${getLogColor(log.type)}`}>
                      <span className="text-slate-500 text-[10px] mr-2">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </span>
                      <span className={`font-medium mr-2 ${log.type === 'error' ? 'text-red-500' : log.type === 'warn' ? 'text-yellow-500' : 'text-blue-400'}`}>
                        [{log.type.toUpperCase()}]
                      </span>
                      <span className="whitespace-pre-wrap break-all">{log.message}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
